import moment, { Moment } from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RcFile } from 'antd/lib/upload';
import {
  Form,
  Modal,
  Button,
  TextInput,
  HoursInput,
  SelectInput,
  ButtonsWrapper,
  TimePickerInput,
  PhoneNumberInput,
  WorkingDaysSelect,
  AvatarInput,
  Typography,
  PLATFORM_THEME_COLORS,
} from '@thinknet-gmbh/platform-common-ui';
import { DEFAULT_NAME_FIELD_REGEX } from '@thinknet-gmbh/platform-common';

import { useCurrentMemberContext } from 'src/contexts';
import { useGetGantts } from 'src/hooks/gantts/get-gantts.hook';
import { useCreateEmployee } from 'src/hooks/employees/create-employee.hook';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { checkIfNewRangeIsValid } from 'src/pages/employee/sub-pages/employee-details/components/employee-details-edit/employee-details-edit.helpers';

import { TimeCopyButton } from './components/time-copy-button';
import { FormContainer, WorkingDaysWrapper, AvatarWrapper } from './create-employee-modal.styles';

const CreateEmployeeModal = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation(['common', 'employee']);
  const [form] = Form.useForm();
  const [selectedDays, setSelectedDays] = useState<{ day: string; value: Moment[] | null }[]>([]);
  const [avatarUrl, setAvatarUrl] = useState<RcFile>();

  const currentCompanyId = useGetCurrentCompanyId();
  const { gantts } = useGetGantts();
  const { ganttsAccess = [] } = useCurrentMemberContext();

  const createEmployee = useCreateEmployee({ onSuccess: onClose });

  useEffect(() => {
    if (selectedDays) {
      selectedDays.forEach(({ day, value }) => {
        form.setFieldsValue({ workingDays: { [day]: value } });
      });
    }
  }, [selectedDays, form]);

  const onFinish = (values: any) => {
    const validatedFields = Object.entries(values.workingDays).map(([key, range]: any) => {
      const errors = [];

      if (values.breakTime && !checkIfNewRangeIsValid(range, values.breakTime)) {
        errors.push(t('common:validation.error.wrongWorkingRange'));
      }

      if (range && range[0].format('HH:mm') === range[1].format('HH:mm')) {
        errors.push(t('common:validation.error.workingRangeSame'));
      }

      return { name: ['workingDays', key], errors };
    });

    const someError = validatedFields.some(field => !!field.errors.length);

    if (someError) {
      return form.setFields(validatedFields);
    }

    const workingDays: any = { breakTime: values.breakTime };

    Object.keys(values.workingDays).forEach(day => {
      workingDays[day] = {
        from: moment(values.workingDays[day][0]).milliseconds(0).toDate(),
        to: moment(values.workingDays[day][1]).milliseconds(0).toDate(),
      };
    });

    createEmployee({
      variables: {
        payload: {
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          ganttId: values.gantt,
          phoneNumber: values.phoneNumber,
          workingDays,
          companyId: currentCompanyId,
          avatar: avatarUrl,
          nickname: values.nickname,
        },
      },
    });
  };

  return (
    <Modal isOpened onClose={onClose} title={t('employee:buttons.addNewEmployee')} modalWidth={644}>
      <Form layout="vertical" onFinish={onFinish} form={form}>
        <AvatarWrapper>
          <Typography fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
            {t('common:labels.avatar')}
          </Typography>
          <AvatarInput
            avatar={avatarUrl}
            onChange={setAvatarUrl}
            btnText={t(`common:buttons.${avatarUrl ? 'reload' : 'upload'}`)}
          />
        </AvatarWrapper>
        <FormContainer>
          <div>
            <Form.Item
              name="firstName"
              label={t('common:labels.firstName')}
              rules={[
                { required: true, message: t('common:validation.error.firstNameRequired') },
                {
                  pattern: DEFAULT_NAME_FIELD_REGEX,
                  message: t('common:validation.error.incorrectFormat'),
                },
              ]}
            >
              <TextInput placeholder="Bill" maxLength={50} />
            </Form.Item>
            <Form.Item
              name="lastName"
              label={t('common:labels.lastName')}
              rules={[
                { required: true, message: t('common:validation.error.lastNameRequired') },
                {
                  pattern: DEFAULT_NAME_FIELD_REGEX,
                  message: t('common:validation.error.incorrectFormat'),
                },
              ]}
            >
              <TextInput placeholder="Sanders" maxLength={50} />
            </Form.Item>
            <Form.Item
              name="nickname"
              label={t('employee:labels.nickname')}
              rules={[
                { required: true, message: t('employee:validationError.nicknameRequired') },
                {
                  pattern: DEFAULT_NAME_FIELD_REGEX,
                  message: t('common:validation.error.incorrectFormat'),
                },
              ]}
            >
              <TextInput placeholder="billsander" maxLength={50} />
            </Form.Item>
            <Form.Item
              name="days"
              label={t('employee:labels.workingDays')}
              rules={[
                { required: true, message: t('employee:validationError.workingDaysRequired') },
              ]}
            >
              <WorkingDaysSelect
                t={t}
                selectedDays={Object.assign(
                  {},
                  ...selectedDays.map(item => ({ [item.day]: true }))
                )}
                onChange={e => {
                  setSelectedDays(
                    e.map((item: string) => {
                      const selectedDay = selectedDays.find(({ day }) => day === item);
                      if (!selectedDay) {
                        form.resetFields(['workingDays', item]);
                      }

                      return { day: item, value: selectedDay?.value || null };
                    })
                  );
                }}
                placeholder={t('employee:labels.selectWorkingDays')}
              />
            </Form.Item>
          </div>
          <div>
            <Form.Item
              name="phoneNumber"
              label={t('common:labels.phoneNumber')}
              rules={[
                { required: true, message: t('common:validation.error.phoneNumberRequired') },
              ]}
            >
              <PhoneNumberInput placeholder="+49 176 384 15749" />
            </Form.Item>
            <Form.Item
              name="email"
              label={t('common:labels.email')}
              rules={[{ type: 'email', message: t('common:validation.error.incorrectFormat') }]}
            >
              <TextInput placeholder="bill.sanders@example.com" maxLength={50} />
            </Form.Item>
            <Form.Item
              name="gantt"
              label={t('employee:labels.ganttName')}
              rules={[{ required: true, message: t('employee:validationError.ganttNameRequired') }]}
            >
              <SelectInput placeholder={t('employee:labels.selectNeededGantt')}>
                {ganttsAccess.map(({ ganttId }: any) => {
                  const { name } = gantts.find(({ _id }: any) => _id === ganttId) || {};

                  return (
                    <SelectInput.Option key={ganttId} value={ganttId}>
                      {name}
                    </SelectInput.Option>
                  );
                })}
              </SelectInput>
            </Form.Item>
            <Form.Item
              name="breakTime"
              label={t('employee:labels.breakTime')}
              rules={[{ required: true, message: t('employee:validationError.breakTimeRequired') }]}
            >
              <HoursInput placeholder="1 hr" />
            </Form.Item>
          </div>
        </FormContainer>
        <WorkingDaysWrapper>
          {selectedDays.map(({ day }) => (
            <Form.Item
              key={day}
              name={['workingDays', day]}
              label={t(`common:days.${day}`)}
              rules={[
                {
                  required: true,
                  message: t('employee:validationError.dayRequired', {
                    day: t(`common:days.${day}`),
                  }),
                },
              ]}
            >
              <TimePickerInput.RangePicker
                format="HH:mm"
                onChange={(value: any) =>
                  setSelectedDays(oldValue => [
                    ...oldValue.map(item => (item.day === day ? { day, value } : item)),
                  ])
                }
              />
            </Form.Item>
          ))}
        </WorkingDaysWrapper>
        {selectedDays.length > 0 && selectedDays.some(({ value }) => !!value) && (
          <TimeCopyButton selectedDays={selectedDays} setSelectedDays={setSelectedDays} />
        )}
        <ButtonsWrapper>
          <Button htmlType="submit" type="primary">
            {t('common:buttons.save')}
          </Button>
          <Button onClick={onClose}>{t('common:buttons.cancel')}</Button>
        </ButtonsWrapper>
      </Form>
    </Modal>
  );
};

export default CreateEmployeeModal;
